import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Stack,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { analyticsApi } from '../../services/api';

const IntentMonitor = () => {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [expandedIntent, setExpandedIntent] = useState(null);

  useEffect(() => {
    fetchIntentStats();
  }, [days]);

  const fetchIntentStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await analyticsApi.getIntentStatistics(days);
      setIntents(data);
    } catch (err) {
      console.error('Error fetching intent statistics:', err);
      setError('Errore nel caricamento delle statistiche intent. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandClick = (intent) => {
    setExpandedIntent(expandedIntent === intent ? null : intent);
  };

  const getIntentLabel = (intent) => {
    const labels = {
      'line_activation': '📱 Attivazione Linea',
      'business_affiliation': '🏢 Affiliazione Business'
    };
    return labels[intent] || intent;
  };

  const getStatusColor = (status) => {
    if (status.includes('pending') || status.includes('review')) return 'warning';
    if (status.includes('completed') || status.includes('success')) return 'success';
    if (status.includes('cancelled') || status.includes('failed')) return 'error';
    return 'default';
  };

  const getStatusLabel = (status) => {
    if (status.includes('pending') || status.includes('review')) return 'In Attesa';
    if (status.includes('completed') || status.includes('success')) return 'Completato';
    if (status.includes('cancelled') || status.includes('failed')) return 'Annullato';
    return status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Caricamento statistiche intent...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header con controlli */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          📊 Monitoraggio Intent
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Periodo</InputLabel>
            <Select
              value={days}
              label="Periodo"
              onChange={(e) => setDays(e.target.value)}
            >
              <MenuItem value={7}>Ultimi 7 giorni</MenuItem>
              <MenuItem value={30}>Ultimi 30 giorni</MenuItem>
              <MenuItem value={90}>Ultimi 90 giorni</MenuItem>
              <MenuItem value={365}>Ultimo anno</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Aggiorna dati">
            <IconButton onClick={fetchIntentStats} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Cards di riepilogo */}
      <Grid container spacing={3} mb={3}>
        {intents.map((intentData) => (
          <Grid item xs={12} md={6} key={intentData.intent}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {getIntentLabel(intentData.intent)}
                  </Typography>
                  <Chip
                    label={`${intentData.total_count} totali`}
                    color="primary"
                    size="small"
                  />
                </Box>

                {/* Statistiche principali */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                      <Typography variant="h5" color="success.main">
                        {intentData.completed_count}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Completati
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <PendingIcon color="warning" sx={{ fontSize: 32 }} />
                      <Typography variant="h5" color="warning.main">
                        {intentData.pending_count}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        In Attesa
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <CancelIcon color="error" sx={{ fontSize: 32 }} />
                      <Typography variant="h5" color="error.main">
                        {intentData.cancelled_count}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Annullati
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Completion rate */}
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Tasso di Completamento
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {intentData.completion_rate}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={intentData.completion_rate}
                    color={intentData.completion_rate >= 70 ? 'success' : intentData.completion_rate >= 40 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Bottone per espandere dettagli */}
                <Box textAlign="center">
                  <IconButton
                    onClick={() => handleExpandClick(intentData.intent)}
                    size="small"
                  >
                    {expandedIntent === intentData.intent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography variant="caption" ml={1}>
                      {expandedIntent === intentData.intent ? 'Nascondi' : 'Mostra'} richieste recenti
                    </Typography>
                  </IconButton>
                </Box>

                {/* Lista richieste recenti (collapsible) */}
                <Collapse in={expandedIntent === intentData.intent}>
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Richieste Recenti:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {intentData.recent_requests.map((request) => (
                            <TableRow key={request.id}>
                              <TableCell>
                                <Typography variant="caption">
                                  {new Date(request.created_at).toLocaleDateString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {request.customer_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {request.customer_email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(request.status)}
                                  color={getStatusColor(request.status)}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Messaggio se nessun intent trovato */}
      {intents.length === 0 && (
        <Alert severity="info">
          Nessun intent registrato negli ultimi {days} giorni.
        </Alert>
      )}
    </Box>
  );
};

export default IntentMonitor;
